// Copyright 2022, Durable.
import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import "./app.css";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from "@mui/material";

import "@fontsource/ubuntu";
import "@fontsource/ubuntu-mono";
import lightTheme from "./components/shared/durableThemeLight";
import darkTheme from "./components/shared/durableThemeDark";
import useLocalStorageState from "use-local-storage-state";
import LoadingPage from "./pages/HardLoad";
import { DevToolWrapper } from "./layouts/DevToolWrapper";
import { DurableCopyright } from "./layouts/Copyright";
import {
  AuthContext,
  ProtectedRoute,
  ProfileContext,
  useAuthContext,
  useProfileContext
} from "./components/shared/Auth";
import { LoginPage } from "./pages/LoginPage";
import { UserPermission, UserService } from "@durable/durable-client";
import { NotificationProvider } from "./NotificationProvider";
import { NotificationBubbles } from "./layouts/NotificationBubbles";
import RouteWrapper from "./layouts/RouteWrapper";
import { LocalStorageLocation } from "./LocalStorage";
import { Navigation } from "./layouts/Navigation";
import supportPages from "./routes/supportPages";
import userPages from "./routes/userPages";
import PageNotFound from "./pages/PageNotFound";
import { IS_CHROME } from "@lexical/utils";

const DebugPlanner = React.lazy(() => import("./pages/DebugPlanner"));

export const ClientContext = React.createContext(null);

interface PasswordSetupModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * A modal component that allows users to set up their password for the first
 * time. The modal enforces password matching and minimum length requirements.
 *
 * @param {Object} props - The component props
 * @param {boolean} props.open - Whether the modal is open or not
 * @param {Object} props.user - The user object for whom the password is being
 * set
 * @param {Function} props.onClose - Callback function to be called when the
 * password setup is complete
 *
 * @returns {JSX.Element} A dialog component for password setup
 *
 * @example
 * <PasswordSetupModal
 *   open={showPasswordModal}
 *   onClose={() => setShowPasswordModal(false)}
 * />
 */
function PasswordSetupModal({ open, onClose }: PasswordSetupModalProps) {
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const authValue = useAuthContext();

  const handleSubmit = async (): Promise<void> => {
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await UserService.updateUserPassword({
        /*
         * The current password is empty because the user is setting a new
         * password for the first time. This is a special case handled by the
         * API.
         */
        current_password: "",
        password: password
      });
      // Update the user object in auth context to reflect password is set.
      if (authValue && authValue.refreshUser) {
        await authValue.refreshUser();
      }
      onClose();
    } catch (err: any) {
      setError("Failed to set password: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={null} disableEscapeKeyDown>
      <DialogTitle>Set Your Password</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Welcome to Durable! Please set a password for your account.
        </DialogContentText>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <TextField
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outlined"
          />
          <TextField
            margin="dense"
            label="Confirm Password"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            variant="outlined"
            error={!!error}
            helperText={error}
          />
          <DialogActions>
            <Button type="submit" disabled={loading}>
              {loading ? "Setting Password..." : "Set Password"}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface UnsupportedBrowserDialogProps {
  open: boolean;
}

/**
 * A modal component that informs users when they're using an unsupported
 * browser. This dialog cannot be closed.
 *
 * @param {Object} props - The component props
 * @param {boolean} props.open - Whether the modal is open or not
 *
 * @returns {JSX.Element} A dialog component for unsupported browser
 * notification
 *
 * @example
 * <UnsupportedBrowserDialog
 *   open={isUnsupportedBrowser}
 * />
 */
function UnsupportedBrowserDialog({ open }: UnsupportedBrowserDialogProps) {
  return (
    <Dialog open={open} disableEscapeKeyDown>
      <DialogTitle>Unsupported Browser</DialogTitle>
      <DialogContent>
        <DialogContentText>
          We currently only support Google Chrome. If possible, please switch
          to Chrome for the best experience with Durable. Support for
          additional browsers is coming soon.
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
}

/** Routes that can make use of authentication. */
function AuthRoutes() {
  const [hasLoaded, setHasLoaded] = useState(false);

  const authValue = useAuthContext();

  // State to determine if modal should be shown
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  /**
   * After the dom is done building, gives 300 ms for pop in to finish, then
   * shows the page.
   */
  useEffect(() => {
    setTimeout(() => {
      setHasLoaded(true);
    }, 300);
  }, []);

  // Check if user is logged in but has no password set
  // Only run this effect when authValue is available and has a user
  useEffect(() => {
    if (!authValue || !authValue.user) {
      setShowPasswordModal(false);
      return;
    }

    setShowPasswordModal(authValue.user.has_password === false);
  }, [authValue, authValue?.user?.has_password]);

  const handlePasswordModalClose = () => {
    // Only allow closing if password was successfully set
    // The modal component will call this when password is set
    setShowPasswordModal(false);
  };

  /**
   * All the user-facing /app/ routes, routed from userPages.
   */
  function appRoutes() {
    return (
      <>
        {userPages.map(
          (page) =>
            !page.dontRoute && (
              <Route
                key={page.id}
                path={page.route}
                element={<RouteWrapper>{page.element()}</RouteWrapper>}
              />
            )
        )}
      </>
    );
  }

  /**
   * The developer-only routes, as above.
   */
  function devRoutes() {
    return (
      <>
        {supportPages.map(
          (devTool) =>
            !devTool.dontRoute && (
              <Route
                key={devTool.id}
                path="/"
                element={
                  <RouteWrapper>
                    <DevToolWrapper toolId={devTool.id} />
                  </RouteWrapper>
                }
              >
                <Route path={devTool.route} element={devTool.element()} />
              </Route>
            )
        )}
      </>
    );
  }

  const protectedRoutes = useMemo(
    () => (
      <Route path="/" element={<ProtectedRoute />}>
        {/* Things that don't have a nav drawer  */}
        <Route
          path="/app/util/planner"
          element={
            <RouteWrapper>
              <DebugPlanner />
            </RouteWrapper>
          }
        />
        {/* Things that do have a nav drawer and user controls */}
        <Route path="/" element={<NotificationBubbles />}>
          <Route path="/" element={<LoadingPage loading={!hasLoaded} />}>
            <Route path="/" element={<Navigation />}>
              {appRoutes()}
              {authValue.user &&
                authValue.user.permissions.includes(
                  UserPermission.DEBUG_TOOLS
                ) &&
                devRoutes()}
              <Route path="*" element={<PageNotFound />} />
            </Route>
          </Route>
        </Route>
      </Route>
    ),
    [authValue.user, hasLoaded]
  );

  const routesComponent = useMemo(
    () => (
      <Routes>
        <Route path="/" element={<DurableCopyright />}>
          {protectedRoutes}
          <Route path="app/login" element={<LoginPage />} />
        </Route>
      </Routes>
    ),
    [protectedRoutes]
  );

  return (
    <AuthContext.Provider value={authValue}>
      <BrowserRouter>
        {/* Password modal - only show if authValue and user exist */}
        {authValue?.user && (
          <PasswordSetupModal
            open={showPasswordModal}
            onClose={handlePasswordModalClose}
          />
        )}
        {routesComponent}
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

/**
 * Build out the rendering tree for our application.
 * Pages under the navbar will have the navigation bar inserted at the top.
 * Most routes are added from the /routes/ directory, and should not be manually
 * added here unless required.
 *
 * @returns {JSX.Element} The main application component
 */
function App() {
  // Always use dark mode
  const [darkMode, setDarkMode] = useLocalStorageState<boolean>(
    LocalStorageLocation.DarkMode,
    {
      defaultValue: true
    }
  );

  // State to track if the user is using an unsupported browser.
  const [isUnsupportedBrowser, setIsUnsupportedBrowser] =
    useState<boolean>(false);

  useEffect(() => {
    setIsUnsupportedBrowser(IS_CHROME === false);
  }, []);

  const profilevalue = useProfileContext();

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-color-mode",
      darkMode ? "dark" : "light"
    );
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  });

  return (
    <ClientContext.Provider value={setDarkMode}>
      <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
        <NotificationProvider>
          {/* Display browser compatibility warning for non-Chrome browsers */}
          <UnsupportedBrowserDialog open={isUnsupportedBrowser} />
          <CssBaseline />
          <div
            className={`${darkMode ? "dark" : "light"}
          accent-durableTealDark-300 selection:bg-durableTeal-300/50`}
          >
            <ProfileContext.Provider value={profilevalue}>
              <AuthRoutes />
            </ProfileContext.Provider>
          </div>
        </NotificationProvider>
      </ThemeProvider>
    </ClientContext.Provider>
  );
}

export default App;
